/* @flow */
import apiRequest from 'lib/apiRequest';
import { clone } from 'lodash';
import Entity from 'lib/Entity';
import Chat from 'entities/Chat';
import Group from 'entities/Group';
import Organizations from 'collections/Organizations';
import Subscriber from 'entities/Subscriber';
import Subscription from 'entities/Subscription';
import Subscriptions from 'collections/Subscriptions';

const groupIsNotStaffClass = (s) => !s.getGroup().isStaffClass();

class Relationship extends Entity {
  initialize() {
    this.urlRoot = '/relationships';
  }

  parse(response: Object) {
    const relationship = clone(response);
    relationship['subscriptions_to_me'] =
      Subscriptions.fromJSON(relationship['subscriptions_to_me']);
    relationship['subscriptions_to_user'] =
      Subscriptions.fromJSON(relationship['subscriptions_to_user']);
    relationship['related_user'] = Subscriber.fromJSON(relationship['related_user']);
    if (relationship['chat']) {
      relationship['chat'] = Chat.fromJSON(relationship['chat']);
    }
    relationship['id'] = relationship['related_user']['id'];
    relationship['common_organizations_with_user'] = Organizations.fromJSON(
        relationship['common_organizations_with_user']
    );
    return relationship;
  }

  getRelatedUser(): Subscriber {
    return this.get('related_user');
  }

  getSubscriptionsToMe() {
    return new Subscriptions(this.get('subscriptions_to_me').filter(groupIsNotStaffClass));
  }

  getSubscriptionsToUser() {
    return new Subscriptions(this.get('subscriptions_to_user').filter(groupIsNotStaffClass));
  }

  getCommonOrganizationsWithUser() {
    return this.get('common_organizations_with_user');
  }

  getAdminAtOrgIds() {
    return this.get('admin_at_org_ids') || [];
  }

  getChat() {
    return this.get('chat');
  }

  unsubscribe(subscription: Subscription) {
    [this.get('subscriptions_to_me'), this.get('subscriptions_to_user')].forEach(collection => {
      collection.remove(subscription.id);
    });
  }

  unsubscribeFromGroup(group: Group) {
    [this.get('subscriptions_to_me'), this.get('subscriptions_to_user')].forEach(collection => {
      collection.removeSubscriptionForGroup(group);
    });
  }

  hasSubscriptions() {
    return (
      !this.getSubscriptionsToMe().isEmpty() ||
      !this.getSubscriptionsToUser().isEmpty()
    );
  }

  getChatEligibilityState() {
    return this.get('chat_eligibility_state');
  }

  isEligibleForChat() {
    return this.getChatEligibilityState() === 'eligible';
  }

  isPendingForChat() {
    return this.getChatEligibilityState() === 'pending';
  }

  isUnder13() {
    return this.getChatEligibilityState() === 'under_13';
  }

  isUnavailableForChat() {
    return this.getChatEligibilityState() === 'unavailable';
  }

  isAgeUnknown() {
    return this.getChatEligibilityState() === 'unknown';
  }

  getOrgAffiliationViews() {
    return this.get('organization_affiliations');
  }

  static updateName(subscriberId, firstName, lastName) {
    return apiRequest(`/relationships/${subscriberId}`, 'PATCH', {
      related_user: {
        first_name: firstName,
        last_name: lastName,
      },
    }).then(response => Relationship.fromJSON(response));
  }

  static updateNote(subscriberId, note) {
    return apiRequest(`/relationships/${subscriberId}`, 'PATCH', {
      related_user: {
        note,
      },
    }).then(response => Relationship.fromJSON(response));
  }

  static confirmUntrustedName(subscriberId) {
    return apiRequest(`/relationships/${subscriberId}`, 'PATCH', {
      related_user: {
        untrusted_name: false,
      },
    }).then(response => Relationship.fromJSON(response));
  }

  static block(relatedUserId) {
    return apiRequest(`/relationships/${relatedUserId}`, 'PATCH', {
      related_user: {
        blocked: true,
      },
    }).then(response => Relationship.fromJSON(response));
  }

  static unblock(relatedUserId) {
    return apiRequest(`/relationships/${relatedUserId}`, 'PATCH', {
      related_user: {
        blocked: false,
      },
    }).then(response => Relationship.fromJSON(response));
  }
}

export default Relationship;



// WEBPACK FOOTER //
// ./src/entities/Relationship.js